import { t } from './i18n.js';

export class TypingTestService extends EventTarget {
    constructor() {
        super();
        this.STORAGE_KEY = 'cytific_typing_best';
        this.isActive = false;
        this.testText = t('TYPING_TEST_PANGRAM', "The quick brown fox jumps over the lazy dog");
        this.currentInput = "";
        this.startTime = null;
        this.endTime = null;
        this.totalTyped = 0;
        this.errors = 0;
        this.personalBest = this.loadBest();
    }

    loadBest() {
        try {
            const saved = localStorage.getItem(this.STORAGE_KEY);
            return saved ? JSON.parse(saved) : { wpm: 0, accuracy: 0 };
        } catch (e) { return { wpm: 0, accuracy: 0 }; }
    }

    saveBest(results) {
        if (results.wpm >= this.personalBest.wpm) {
            this.personalBest = {
                wpm: results.wpm,
                accuracy: Math.max(this.personalBest.accuracy, results.accuracy)
            };
            try {
                localStorage.setItem(this.STORAGE_KEY, JSON.stringify(this.personalBest));
            } catch (e) { /* ignore */ }
        }
    }

    start(customText = null) {
        if (customText) this.testText = customText;
        this.isActive = true;
        this.currentInput = "";
        this.startTime = Date.now();
        this.endTime = null;
        this.totalTyped = 0;
        this.errors = 0;
        this.dispatchEvent(new CustomEvent('teststart'));
    }

    stop() {
        if (!this.isActive) return;
        this.isActive = false;
        this.endTime = Date.now();
        const results = this.getResults();
        this.saveBest(results);
        this.dispatchEvent(new CustomEvent('testcomplete', { detail: results }));
    }

    reset() {
        this.isActive = false;
        this.currentInput = "";
        this.startTime = null;
        this.endTime = null;
        this.totalTyped = 0;
        this.errors = 0;
        this.dispatchEvent(new CustomEvent('testreset'));
    }

    processKey(keyString) {
        if (!this.isActive) {
            // Start test automatically on first printable character if not active
            if (keyString.length === 1 && !keyString.match(/Control|Meta|Alt|Shift/)) {
                this.start();
            } else {
                return;
            }
        }

        // Handle Backspace
        if (keyString === 'Backspace') {
            if (this.currentInput.length > 0) {
                this.currentInput = this.currentInput.slice(0, -1);
            }
            this.dispatchEvent(new CustomEvent('testupdate', { detail: this.getResults() }));
            return;
        }

        // Only process printable single characters
        if (keyString.length !== 1) return;

        this.totalTyped++;
        const expectedChar = this.testText[this.currentInput.length];

        if (keyString === expectedChar) {
            this.currentInput += keyString;
        } else {
            this.errors++;
            // Still append it so they have to backspace, or just count error and ignore?
            // Standard typing tests force you to correct, so we append the wrong char.
            this.currentInput += keyString;
        }

        this.dispatchEvent(new CustomEvent('testupdate', { detail: this.getResults() }));

        // Check completion condition
        if (this.currentInput.length === this.testText.length) {
            if (this.currentInput === this.testText) {
                this.stop();
            }
        }
    }

    getResults() {
        const now = this.endTime || Date.now();
        let timeElapsedMinutes = 0;
        let wpm = 0;

        if (this.startTime && this.totalTyped > 0) {
            timeElapsedMinutes = (now - this.startTime) / 60000;
            // Standard WPM calculation: (Characters / 5) / Time in Minutes
            // Subtracting errors calculates "Net WPM"
            const grossWpm = (this.currentInput.length / 5) / timeElapsedMinutes;
            const errorPenalty = this.errors / timeElapsedMinutes;
            wpm = Math.max(0, Math.round(grossWpm - errorPenalty));
        }

        const accuracy = this.totalTyped > 0
            ? Math.max(0, Math.round(((this.totalTyped - this.errors) / this.totalTyped) * 100))
            : 100;

        return {
            isActive: this.isActive,
            text: this.testText,
            input: this.currentInput,
            wpm,
            accuracy,
            errors: this.errors,
            timeElapsedSeconds: this.startTime ? Math.round((now - this.startTime) / 1000) : 0
        };
    }
}

export const typingTestService = new TypingTestService();
